import { normaliseLoginId, sameLoginId } from "@/lib/loginId";

/**
 * Direct-message identity rules, shared by the Messages page and the sidebar.
 *
 * messages.sender_id / recipient_id are LOGIN ids (uuid columns; RLS compares
 * recipient_id with auth.uid()). Everything here works in login ids and
 * compares them case-insensitively — the client may hold an uppercase id
 * (iOS `uuidString`) even though Postgres always returns lowercase.
 */

export interface DirectMessageParties {
  sender_id: string | null;
  recipient_id: string | null;
}

/**
 * For a message the signed-in user can see, the OTHER person's login id
 * (normalised), and whether the signed-in user is its recipient.
 */
export function otherParty(
  msg: DirectMessageParties,
  myLoginId: string | null | undefined,
): { otherLoginId: string | null; iAmRecipient: boolean } {
  const iAmRecipient = sameLoginId(msg.recipient_id, myLoginId);
  return {
    otherLoginId: normaliseLoginId(iAmRecipient ? msg.sender_id : msg.recipient_id),
    iAmRecipient,
  };
}

/** True when the signed-in user sent this message ("is this mine"). */
export function isOwnMessage(msg: Pick<DirectMessageParties, "sender_id">, myLoginId: string | null | undefined): boolean {
  return sameLoginId(msg.sender_id, myLoginId);
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/**
 * PostgREST `.or()` filter that matches every direct message between two
 * people, in either direction. Returns null unless BOTH ids are well-formed
 * uuids — the ids are interpolated into a filter string, so anything else is
 * refused rather than passed through.
 */
export function directPairFilter(
  myLoginId: string | null | undefined,
  otherLoginId: string | null | undefined,
): string | null {
  const me = normaliseLoginId(myLoginId);
  const them = normaliseLoginId(otherLoginId);
  if (!me || !them || !UUID_RE.test(me) || !UUID_RE.test(them) || me === them) return null;
  return `and(sender_id.eq.${me},recipient_id.eq.${them}),and(sender_id.eq.${them},recipient_id.eq.${me})`;
}

/** What a server-side lookup of the existing thread returned. */
export type DirectThreadLookup =
  | { kind: "found"; conversationId: string | null }
  | { kind: "failed" };

export type DirectConversationDecision =
  | { kind: "reuse"; conversationId: string }
  | { kind: "mint"; conversationId: string }
  | { kind: "unavailable" };

/**
 * Which conversation to open when the user starts a chat with someone.
 *
 * The existing thread is looked up SERVER-SIDE by the two login ids (newest
 * message between them, either direction) rather than in the messages already
 * loaded — the page loads only the newest 500, so an older thread, e.g. one
 * started on iOS with a random conversation id, would be missed and a second,
 * parallel thread minted. Web mints the sorted-email-pair id; iOS mints a
 * random uuid; reusing whichever id the newest message carries keeps both
 * platforms in one thread.
 *
 * A failed lookup is NOT treated as "no thread": minting then would create
 * exactly the duplicate this exists to prevent, so the caller reports it.
 */
export function decideDirectConversation(
  lookup: DirectThreadLookup,
  pairConversationId: string,
): DirectConversationDecision {
  if (lookup.kind === "failed") return { kind: "unavailable" };
  const existing = lookup.conversationId?.trim();
  if (existing) return { kind: "reuse", conversationId: existing };
  return { kind: "mint", conversationId: pairConversationId };
}
